import { EntryCollection } from "@/types/entryCollection";
import { orderByReviewPriority } from "@shared/relabelReview";
import { EntryConfig, ENTRY_CONFIG_TYPES } from "@/types/entryConfig";
import { Plot } from "@/types/plot";
import { EntryImage, extractImagesFromEntryCollection } from "@/utils/entryUtils";

export const EDITABLE_ANNOTATION_IMAGE_TYPES = [
  ENTRY_CONFIG_TYPES.STAND_COUNT_MEASUREMENT,
  ENTRY_CONFIG_TYPES.IMAGE_MEASUREMENT,
] as const;

export type SequenceDirection = "prev" | "next";

export interface EditablePlotImageSequenceItem extends EntryImage {
  key: string;
  plotId: string;
  plotDocId?: string;
  traitId?: string;
  userId: string | null;
  userEmail: string;
}

export interface EditableSequenceNavigationTarget {
  plotId: string;
  itemKey: string;
}

export function getEditablePlotImageKey(
  plotId: string,
  image: Pick<EntryImage, "url" | "collectionId" | "sampleKey">
): string {
  return [plotId, image.collectionId ?? "unknown-collection", image.sampleKey, image.url].join("::");
}

export function compareSampleKeys(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

export function orderEditableReviewQueue(items: readonly EditablePlotImageSequenceItem[]): EditablePlotImageSequenceItem[] {
  return orderByReviewPriority(items);
}

/**
 * Keyboard targets that own the keys themselves — viewer/navigation shortcuts must not
 * fire for them. Covers text inputs, selects, contenteditable regions, focused slider
 * thumbs (Radix focuses the thumb on pointerdown, so an arrow press right after a mouse
 * adjustment would otherwise nudge the filter value AND navigate in one keystroke), and
 * anything inside an open alertdialog (a modal prompt — persist / unsaved-changes — traps
 * focus over the viewer, so its keydowns must not reach the viewer's window-level
 * Delete/Ctrl+B/zoom handlers behind it). NOTE: only `[role='alertdialog']`, not
 * `[role='dialog']` — the image-viewer lightbox itself is a `role='dialog'`, so matching
 * that would swallow every key inside the viewer, including its own shortcuts.
 * The ancestor-walking `closest()` also matches descendants of those containers, mirroring
 * the inline keydown guards in the annotation editors, and needs no jsdom shim.
 */
export function shouldIgnoreSequenceNavigationKeys(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(
      target.closest("input, textarea, select, [contenteditable='true'], [role='slider'], [role='alertdialog']")
    )
  );
}

export function isEditableAnnotationImage(image: EntryImage): boolean {
  return Boolean(
    image.captureMetadataCloudPath &&
      EDITABLE_ANNOTATION_IMAGE_TYPES.includes(image.type as (typeof EDITABLE_ANNOTATION_IMAGE_TYPES)[number])
  );
}

/**
 * What the lightbox can step through. Editable types still need capture metadata, so the review
 * queue holds only images a reviewer can act on. Every other type is view-only and browsable as is;
 * `isEditableAnnotationImage` alone decides what can be edited.
 */
export function isSequenceImage(image: EntryImage): boolean {
  const isEditableType = EDITABLE_ANNOTATION_IMAGE_TYPES.includes(
    image.type as (typeof EDITABLE_ANNOTATION_IMAGE_TYPES)[number]
  );
  return !isEditableType || isEditableAnnotationImage(image);
}

export function buildPlotImageSequence({
  plot,
  entryCollections,
  entryConfigs,
  userEmails,
  selectedTraitId,
}: {
  plot: Plot;
  entryCollections: EntryCollection[];
  entryConfigs: EntryConfig[];
  userEmails: Record<string, string>;
  selectedTraitId?: string;
}): EditablePlotImageSequenceItem[] {
  const configIdToName = new Map<string, string>();
  entryConfigs.forEach((config) => {
    if (config.id) {
      configIdToName.set(config.id, config.name || config.id);
    }
  });

  const sortedCollections = [...entryCollections].sort((a, b) => {
    const aTime = a.createdAt?.seconds || 0;
    const bTime = b.createdAt?.seconds || 0;
    return bTime - aTime;
  });

  const userBuckets = new Map<string, EditablePlotImageSequenceItem[]>();
  const orderedUserKeys: string[] = [];

  sortedCollections.forEach((collection) => {
    const traitId = collection.entryConfigRef?.id;
    if (selectedTraitId && traitId !== selectedTraitId) {
      return;
    }

    const traitName = traitId ? configIdToName.get(traitId) : undefined;
    const createdById = collection.createdBy?.id ?? null;

    const sequenceImages = extractImagesFromEntryCollection(collection, traitName)
      .filter(isSequenceImage)
      .sort((a, b) => compareSampleKeys(a.sampleKey, b.sampleKey));

    if (sequenceImages.length === 0) {
      return;
    }

    const userKey = createdById ?? `unknown:${collection.id ?? traitId ?? plot.plotID}`;

    if (!userBuckets.has(userKey)) {
      userBuckets.set(userKey, []);
      orderedUserKeys.push(userKey);
    }

    sequenceImages.forEach((image) => {
      userBuckets.get(userKey)?.push({
        ...image,
        key: getEditablePlotImageKey(plot.plotID, image),
        plotId: plot.plotID,
        plotDocId: plot.id,
        traitId,
        userId: createdById,
        userEmail: createdById ? userEmails[createdById] || "Unknown User" : "Unknown User",
      });
    });
  });

  // Task priority takes precedence. The existing bucket order remains the fallback for unranked images.
  return orderEditableReviewQueue(orderedUserKeys.flatMap((userKey) => userBuckets.get(userKey) || []));
}

export function resolveEditableImageNavigationTarget({
  currentPlotId,
  currentItemKey,
  direction,
  queue,
}: {
  currentPlotId: string;
  currentItemKey: string;
  direction: SequenceDirection;
  queue: readonly EditablePlotImageSequenceItem[];
}): EditableSequenceNavigationTarget | null {
  const index = queue.findIndex((item) => item.plotId === currentPlotId && item.key === currentItemKey);
  if (index < 0) return null;
  const target = queue[index + (direction === "next" ? 1 : -1)];
  return target ? { plotId: target.plotId, itemKey: target.key } : null;
}
